import { z } from "zod";

const displayNameAllowedPattern = /^[\p{L}\p{N} ]+$/u;

const DISPLAY_NAME_MIN_LENGTH = 3;
const DISPLAY_NAME_MAX_LENGTH = 20;

const RESERVED_DISPLAY_NAMES = new Set(["guest"]);

const displayNameCharacterMessage = "Display name can only contain letters, numbers, and spaces.";

const displayNameMinLengthMessage = `Display name must be at least ${DISPLAY_NAME_MIN_LENGTH} characters.`;

const displayNameMaxLengthMessage = `Display name must be at most ${DISPLAY_NAME_MAX_LENGTH} characters.`;

const displayNameReservedMessage = "That display name is reserved.";
function normalizeForReservedCheck(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

export function displayNameHasAllowedCharacters(name: string): boolean {
  return displayNameAllowedPattern.test(name);
}

export function isReservedDisplayName(name: string): boolean {
  return RESERVED_DISPLAY_NAMES.has(normalizeForReservedCheck(name));
}

export function getDisplayNameValidationError(name: string): string | undefined {
  const trimmed = name.trim();

  if (trimmed.length < DISPLAY_NAME_MIN_LENGTH) {
    return displayNameMinLengthMessage;
  }
  if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) {
    return displayNameMaxLengthMessage;
  }
  if (!displayNameHasAllowedCharacters(trimmed)) {
    return displayNameCharacterMessage;
  }
  if (isReservedDisplayName(trimmed)) {
    return displayNameReservedMessage;
  }

  return undefined;
}

export function isValidDisplayName(name: string): boolean {
  return getDisplayNameValidationError(name) === undefined;
}

const displayNameSchema = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN_LENGTH, displayNameMinLengthMessage)
  .max(DISPLAY_NAME_MAX_LENGTH, displayNameMaxLengthMessage)
  .refine(displayNameHasAllowedCharacters, {
    message: displayNameCharacterMessage,
  })
  .refine((name) => !isReservedDisplayName(name), {
    message: displayNameReservedMessage,
  });

export const updateDisplayNameInputSchema = z.object({
  displayName: displayNameSchema,
});
